<template>
    <v-dialog v-model="dialogSwitch" max-width="460" width="95%" scrollable>
        <v-card rounded="lg">
            <v-card-title class="d-flex justify-space-between align-center pa-4 pb-2">
                <span class="text-subtitle-1 font-weight-bold text-blue-grey-darken-2">Trip Recap</span>
                <v-btn icon="mdi-close" size="small" variant="text" @click="dialogSwitch = false"/>
            </v-card-title>

            <v-card-text class="pa-4 pt-2">
                <!-- Hero ring + score message -->
                <div class="d-flex flex-column align-center mb-4">
                    <div class="hero-ring-wrap">
                        <svg width="120" height="120" viewBox="0 0 120 120">
                            <circle
                                cx="60" cy="60" r="52"
                                fill="none"
                                :stroke="hexToRgba(scoreHex, 0.15)"
                                stroke-width="10"
                            />
                            <circle
                                cx="60" cy="60" r="52"
                                fill="none"
                                :stroke="scoreHex"
                                stroke-width="10"
                                :stroke-dasharray="ringDashArray"
                                stroke-linecap="round"
                                transform="rotate(-90 60 60)"
                                style="transition: stroke-dasharray 0.6s ease;"
                            />
                        </svg>
                        <div class="hero-ring-center">
                            <span :style="{color: scoreHex}" class="hero-score">{{ score !== null ? formatTripScore(score) : '-' }}</span>
                            <span class="hero-score-suffix">/10</span>
                        </div>
                    </div>
                    <p class="text-caption text-grey-darken-1 mt-2 text-center">{{ scoreMessage }}</p>
                </div>

                <!-- Top tip - actionable, anchored to the worst event -->
                <div v-if="topTip" class="tip-card mb-4">
                    <v-icon size="20" :color="topTip.color">{{ topTip.icon }}</v-icon>
                    <div class="tip-text">
                        <div class="tip-title">{{ topTip.title }}</div>
                        <div class="tip-body">{{ topTip.body }}</div>
                    </div>
                </div>

                <!-- Stacked event mix - what made up the deductions visually -->
                <div v-if="hasAnyEvents" class="mb-4">
                    <div class="d-flex justify-space-between align-center mb-1">
                        <span class="text-caption font-weight-medium text-blue-grey-darken-1">Where the events landed</span>
                        <span class="text-caption text-grey">{{ totalEventUnits }} {{ totalEventUnits === 1 ? 'event' : 'events' }}</span>
                    </div>
                    <div class="stacked-bar">
                        <div
                            v-for="seg in eventStack"
                            :key="seg.label"
                            class="stacked-bar-seg"
                            :title="`${seg.label}: ${seg.display}`"
                            :style="{width: seg.pct + '%', backgroundColor: seg.color}"
                        />
                    </div>
                    <div class="stacked-bar-legend">
                        <div v-for="seg in eventStack" :key="'leg-' + seg.label" class="stacked-bar-legend-item">
                            <span class="legend-swatch" :style="{backgroundColor: seg.color}"/>
                            <span class="legend-label">{{ seg.label }}</span>
                            <span class="legend-value">{{ seg.display }}</span>
                        </div>
                    </div>
                </div>

                <!-- Detail event cards (kept for at-a-glance context) -->
                <div v-if="hasAnyData" class="text-caption font-weight-medium text-blue-grey-darken-1 mb-1">Trip details</div>

                <v-row dense class="mb-2">
                    <v-col cols="4">
                        <div class="event-card" :style="cardStyle(braking > 0 ? COLOR_BRAKE : '#4CAF50')">
                            <v-icon size="20" :color="braking > 0 ? COLOR_BRAKE : '#4CAF50'">mdi-car-brake-alert</v-icon>
                            <div class="event-value" :style="{color: braking > 0 ? COLOR_BRAKE : '#4CAF50'}">{{ braking }}</div>
                            <div class="event-label">Hard Braking</div>
                            <div v-if="peakBrakeMg > 0" class="event-sub">{{ (peakBrakeMg / 1000).toFixed(2) }}g</div>
                        </div>
                    </v-col>
                    <v-col cols="4">
                        <div class="event-card" :style="cardStyle(accel > 0 ? COLOR_ACCEL : '#4CAF50')">
                            <v-icon size="20" :color="accel > 0 ? COLOR_ACCEL : '#4CAF50'">mdi-rocket-launch</v-icon>
                            <div class="event-value" :style="{color: accel > 0 ? COLOR_ACCEL : '#4CAF50'}">{{ accel }}</div>
                            <div class="event-label">Hard Accel</div>
                            <div v-if="peakAccelMg > 0" class="event-sub">{{ (peakAccelMg / 1000).toFixed(2) }}g</div>
                        </div>
                    </v-col>
                    <v-col cols="4">
                        <div class="event-card" :style="cardStyle(cornering > 0 ? COLOR_TURN : '#4CAF50')">
                            <v-icon size="20" :color="cornering > 0 ? COLOR_TURN : '#4CAF50'">mdi-steering</v-icon>
                            <div class="event-value" :style="{color: cornering > 0 ? COLOR_TURN : '#4CAF50'}">{{ cornering }}</div>
                            <div class="event-label">Hard Turns</div>
                            <div v-if="peakCornerMg > 0" class="event-sub">{{ (peakCornerMg / 1000).toFixed(2) }}g</div>
                        </div>
                    </v-col>
                </v-row>

                <v-row dense>
                    <v-col cols="4">
                        <div class="event-card" :style="cardStyle(speedingSec > 0 ? COLOR_SPEED : '#4CAF50')">
                            <v-icon size="20" :color="speedingSec > 0 ? COLOR_SPEED : '#4CAF50'">mdi-speedometer</v-icon>
                            <div class="event-value" :style="{color: speedingSec > 0 ? COLOR_SPEED : '#4CAF50'}">{{ speedingSec > 0 ? formatDuration(speedingSec) : '0' }}</div>
                            <div class="event-label">Speeding</div>
                        </div>
                    </v-col>
                    <v-col cols="4">
                        <div class="event-card" :style="cardStyle('#1976D2')">
                            <v-icon size="20" color="#1976D2">mdi-gauge</v-icon>
                            <div class="event-value" style="color: #1976D2">{{ maxSpeed }}</div>
                            <div class="event-label">Max Speed</div>
                            <div class="event-sub">{{ maxSpeedSource }}</div>
                        </div>
                    </v-col>
                    <v-col cols="4">
                        <div class="event-card" :style="cardStyle(crash > 0 ? COLOR_CRASH : '#4CAF50')">
                            <v-icon size="20" :color="crash > 0 ? COLOR_CRASH : '#4CAF50'">mdi-car-emergency</v-icon>
                            <div class="event-value" :style="{color: crash > 0 ? COLOR_CRASH : '#4CAF50'}">{{ crash > 0 ? crash : 'None' }}</div>
                            <div class="event-label">Crash</div>
                        </div>
                    </v-col>
                </v-row>

                <!-- No data message -->
                <div v-if="!hasAnyData" class="text-center text-grey my-4">
                    <v-icon size="32" color="grey-lighten-1">mdi-information-outline</v-icon>
                    <p class="text-caption mt-2">No driving event data available for this trip.</p>
                </div>

                <!-- View on map deep link -->
                <div v-if="hasAnyEvents" class="d-flex justify-center mt-4">
                    <v-btn
                        size="small"
                        variant="tonal"
                        color="primary"
                        prepend-icon="mdi-map-marker-radius"
                        @click="emitViewOnMap"
                    >
                        View events on map
                    </v-btn>
                </div>

                <!-- Source Badge -->
                <div v-if="hasAnyData" class="d-flex justify-center mt-3">
                    <v-chip :color="hasFirmware ? 'blue' : 'grey'" size="small" variant="tonal">
                        <v-icon start size="14">{{ hasFirmware ? 'mdi-access-point' : 'mdi-crosshairs-gps' }}</v-icon>
                        {{ hasFirmware ? 'Accelerometer (50Hz)' : 'GPS Estimated' }}
                    </v-chip>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {ref, computed} from "vue";
import {useTripScore} from "@/composables/useTripScore";
import {strOperations} from "@/composables/globals";

const {tripScoreColor, formatTripScore, hasTripScore} = useTripScore();

// Palette must match the trip-route map (TripRouteMap.vue) so the dialog
// reads as the SAME visual language as the route the user is recapping.
const COLOR_CRASH = '#FF1744'; // vivid danger red - readable on light and dark (pure black vanished in dark mode)
const COLOR_BRAKE = '#E53935';
const COLOR_ACCEL = '#8E24AA';
const COLOR_TURN  = '#FF6F00';
const COLOR_SPEED = '#FB8C00';

const props = defineProps({
    modelValue: Boolean,
    trip: Object,
});
const emit = defineEmits(['update:modelValue', 'view-on-map']);
const dialogSwitch = computed({
    get: () => props.modelValue,
    set: (v) => emit('update:modelValue', v),
});

const emitViewOnMap = () => {
    emit('view-on-map', props.trip);
    dialogSwitch.value = false;
};

// Support two API formats: TripResource (nested driver_analytics) and Reports (flat fields)
const da = computed(() => props.trip?.driver_analytics);
const se = computed(() => props.trip?.score_events);
const hasFirmware = computed(() => {
    return (da.value != null) || (props.trip?.harsh_braking_count != null);
});
const hasAnyData = computed(() => hasFirmware.value || se.value != null);

const p = (v) => {
    if (v === null || v === undefined) return 0;
    const n = Number(v);
    return isNaN(n) ? 0 : Math.max(0, n);
};

const field = (name) => da.value?.[name] ?? props.trip?.[name] ?? se.value?.[name];

const score = computed(() => props.trip?.trip_score != null ? parseFloat(props.trip.trip_score) : null);
const braking = computed(() => p(field('harsh_braking_count')));
const accel = computed(() => p(field('harsh_accel_count')));
const cornering = computed(() => p(field('sharp_cornering_count')));
const crash = computed(() => p(field('crash_count')));
const peakBrakeMg = computed(() => p(field('max_brake_mg')));
const peakAccelMg = computed(() => p(field('max_accel_mg')));
const peakCornerMg = computed(() => p(field('max_corner_mg')));
const speedingSec = computed(() => p(field('speeding_seconds')));
const maxSpeedKmhX10 = computed(() => p(field('max_speed_kmh_x10')));

const maxSpeed = computed(() => {
    if (maxSpeedKmhX10.value > 0) {
        return strOperations().unitifyDistance(maxSpeedKmhX10.value / 10, 'speed', 'km');
    }
    return props.trip?.max_speed > 0 ? strOperations().unitifyDistance(props.trip.max_speed) : '-';
});
const maxSpeedSource = computed(() => maxSpeedKmhX10.value > 0 ? 'OBD' : 'GPS');

const scoreHex = computed(() => {
    if (score.value === null) return '#9E9E9E';
    if (score.value >= 8.0) return '#4CAF50';
    if (score.value >= 5.0) return '#FB8C00';
    return '#D32F2F';
});

// Stroke fill for the hero ring. Score is 0-10; map to 0-1 fraction of
// the circle (radius 52, circumference ~327). When score is null, show
// the empty grey track only.
const ringDashArray = computed(() => {
    const C = 2 * Math.PI * 52;
    const frac = score.value === null ? 0 : Math.max(0, Math.min(1, score.value / 10));
    return `${(C * frac).toFixed(1)} ${C.toFixed(1)}`;
});

const scoreMessage = computed(() => {
    if (score.value === null) return 'Not enough data to score this trip.';
    if (score.value >= 9.0) return 'Excellent driving - keep it up.';
    if (score.value >= 7.0) return 'Good trip with minor events.';
    if (score.value >= 5.0) return 'Room for improvement - see the events below.';
    return 'Needs attention. Review the events below.';
});

// Treat one event as one "unit" except speeding which counts as one unit
// per 30s episode-equivalent (so 60s of speeding doesn't dwarf a single
// brake on the bar). Crashes count as 5 units to remain prominent.
const speedingUnits = computed(() => {
    const s = speedingSec.value;
    if (!s) return 0;
    return Math.max(1, Math.round(s / 30));
});
const crashUnits = computed(() => crash.value * 5);

const eventStack = computed(() => {
    const segs = [];
    if (crashUnits.value)   segs.push({label: 'Crash',       color: COLOR_CRASH, units: crashUnits.value, display: `${crash.value}`});
    if (braking.value)      segs.push({label: 'Hard brake',  color: COLOR_BRAKE, units: braking.value, display: `${braking.value}`});
    if (accel.value)        segs.push({label: 'Hard accel',  color: COLOR_ACCEL, units: accel.value, display: `${accel.value}`});
    if (cornering.value)    segs.push({label: 'Hard turn',   color: COLOR_TURN,  units: cornering.value, display: `${cornering.value}`});
    if (speedingUnits.value)segs.push({label: 'Speeding',    color: COLOR_SPEED, units: speedingUnits.value, display: formatDuration(speedingSec.value)});
    const total = segs.reduce((a, b) => a + b.units, 0) || 1;
    return segs.map(s => ({...s, pct: (s.units / total) * 100}));
});

const totalEventUnits = computed(() => braking.value + accel.value + cornering.value + crash.value + (speedingSec.value > 0 ? 1 : 0));

const hasAnyEvents = computed(() => totalEventUnits.value > 0);

// Pick the worst event for the actionable tip. Order matters: a crash
// dominates everything, then brake severity by mg, then peak accel,
// then corner, then speeding total, then a positive nudge if everything
// is clean.
const topTip = computed(() => {
    if (!hasAnyData.value) return null;
    if (crash.value > 0) {
        return {
            icon: 'mdi-car-emergency', color: COLOR_CRASH,
            title: 'A crash event was detected',
            body: 'Confirm the driver is OK. The map below pinpoints the impact location.',
        };
    }
    if (peakBrakeMg.value >= 600) {
        return {
            icon: 'mdi-car-brake-alert', color: COLOR_BRAKE,
            title: `Hardest brake hit ${(peakBrakeMg.value / 1000).toFixed(2)}g`,
            body: 'Try leaving more following distance - most hard brakes are reactive, not necessary.',
        };
    }
    if (peakAccelMg.value >= 500) {
        return {
            icon: 'mdi-rocket-launch', color: COLOR_ACCEL,
            title: `Peak acceleration was ${(peakAccelMg.value / 1000).toFixed(2)}g`,
            body: 'Easing into the throttle saves fuel and reduces wear.',
        };
    }
    if (peakCornerMg.value >= 600) {
        return {
            icon: 'mdi-steering', color: COLOR_TURN,
            title: `A turn pulled ${(peakCornerMg.value / 1000).toFixed(2)}g lateral`,
            body: 'Slow down before the turn rather than mid-corner.',
        };
    }
    if (speedingSec.value > 0) {
        return {
            icon: 'mdi-speedometer', color: COLOR_SPEED,
            title: `${formatDuration(speedingSec.value)} above the limit`,
            body: 'Cruise control helps maintain a steady speed on highway stretches.',
        };
    }
    if (braking.value > 0 || accel.value > 0 || cornering.value > 0) {
        return {
            icon: 'mdi-information-outline', color: '#1976D2',
            title: 'A few minor events',
            body: 'Nothing severe - see the timeline on the map for context.',
        };
    }
    return {
        icon: 'mdi-thumb-up', color: '#4CAF50',
        title: 'Clean trip',
        body: 'No driver-analytics events were flagged. Nice work.',
    };
});

const hexToRgba = (hex, alpha) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const cardStyle = (color) => ({
    backgroundColor: hexToRgba(color, 0.05),
    border: `1px solid ${hexToRgba(color, 0.15)}`,
    borderRadius: '10px',
    padding: '10px 6px',
    textAlign: 'center',
    minHeight: '90px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
});

function formatDuration(seconds) {
    const s = parseInt(seconds);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m < 60) return `${m}m${rs > 0 ? ` ${rs}s` : ''}`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h${rm > 0 ? ` ${rm}m` : ''}`;
}
</script>

<style scoped>
.hero-ring-wrap {
    position: relative;
    width: 120px;
    height: 120px;
}
.hero-ring-center {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}
.hero-score {
    font-size: 36px;
    font-weight: 700;
    line-height: 1;
}
.hero-score-suffix {
    font-size: 11px;
    color: #999;
    margin-top: 2px;
}

.tip-card {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 10px 12px;
    background: rgba(var(--v-theme-on-surface), 0.05);
    border-radius: 10px;
    border: 1px solid rgba(var(--v-theme-on-surface), 0.12);
}
.tip-text {
    flex: 1;
    min-width: 0;
}
.tip-title {
    font-size: 12px;
    font-weight: 700;
    color: rgb(var(--v-theme-on-surface));
    margin-bottom: 2px;
}
.tip-body {
    font-size: 12px;
    color: rgba(var(--v-theme-on-surface), 0.7);
    line-height: 1.4;
}

.stacked-bar {
    display: flex;
    height: 12px;
    border-radius: 999px;
    overflow: hidden;
    background: rgba(var(--v-theme-on-surface), 0.12);
}
.stacked-bar-seg {
    height: 100%;
}
.stacked-bar-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 12px;
    margin-top: 6px;
}
.stacked-bar-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: rgba(var(--v-theme-on-surface), 0.7);
}
.legend-swatch {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    display: inline-block;
}
.legend-label {
    color: rgb(var(--v-theme-on-surface));
    font-weight: 600;
}
.legend-value {
    color: rgba(var(--v-theme-on-surface), 0.7);
}

.event-value {
    font-size: 16px;
    font-weight: 700;
    margin: 2px 0;
}
.event-label {
    font-size: 10px;
    color: rgba(var(--v-theme-on-surface), 0.6);
}
.event-sub {
    font-size: 9px;
    color: #BDBDBD;
}
</style>
